import React, { useState, useCallback } from 'react'
import { X, Download, Phone, Calendar } from 'react-feather'
import axios from 'axios'
import baseUrl from '../../Utils/baseUrl'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface CallRecord {
  call_id: string
  timestamp: string
  date: string
  time: string
  phone_number: string
  duration: string
  answered_by: string
}

interface CallSheetModalProps {
  isOpen: boolean
  onClose: () => void
}

type RangePreset = 'today' | '7d' | '30d' | 'custom'

const formatPhone = (num: string): string => {
  const digits = num.replace(/\D/g, '')
  const last10 = digits.length > 10 ? digits.slice(-10) : digits
  if (last10.length === 10) {
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`
  }
  return num
}

const CallSheetModal: React.FC<CallSheetModalProps> = ({ isOpen, onClose }) => {
  const [rangePreset, setRangePreset] = useState<RangePreset>('7d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [records, setRecords] = useState<CallRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [error, setError] = useState('')

  const getDateRange = useCallback((): { start: string; end: string } => {
    const today = new Date()
    const fmt = (d: Date) => d.toISOString().slice(0, 10)

    switch (rangePreset) {
      case 'today':
        return { start: fmt(today), end: fmt(today) }
      case '7d': {
        const s = new Date(today)
        s.setDate(s.getDate() - 6)
        return { start: fmt(s), end: fmt(today) }
      }
      case '30d': {
        const s = new Date(today)
        s.setDate(s.getDate() - 29)
        return { start: fmt(s), end: fmt(today) }
      }
      case 'custom':
        return { start: customStart, end: customEnd }
      default:
        return { start: fmt(today), end: fmt(today) }
    }
  }, [rangePreset, customStart, customEnd])

  const fetchRecords = useCallback(async () => {
    const { start, end } = getDateRange()
    if (!start || !end) {
      setError('Please select both start and end dates')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await axios.get(`${baseUrl}/intake/call_sheet`, {
        params: { start, end },
      })
      setRecords(res.data.data || [])
      setFetched(true)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to fetch call records')
      setRecords([])
    } finally {
      setLoading(false)
    }
  }, [getDateRange])

  const handleClose = useCallback(() => {
    setRecords([])
    setFetched(false)
    setError('')
    setRangePreset('7d')
    setCustomStart('')
    setCustomEnd('')
    onClose()
  }, [onClose])

  const downloadCSV = useCallback(() => {
    const headers = ['Timestamp', 'Duration', 'Caller Number']
    const escape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n'))
        return `"${v.replace(/"/g, '""')}"`
      return v
    }
    const lines = [
      headers.join(','),
      ...records.map((r) =>
        [escape(r.timestamp), escape(r.duration || ''), escape(formatPhone(r.phone_number))].join(',')
      ),
    ]
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const { start, end } = getDateRange()
    link.download = `call_report_${start}_to_${end}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [records, getDateRange])

  const downloadPDF = useCallback(() => {
    const doc = new jsPDF()
    const { start, end } = getDateRange()
    const pageWidth = doc.internal.pageSize.getWidth()

    // -- Logo --
    const logoBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABTsAAAC5CAYAAADnCusiAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAOeJJREFUeNrs3Q+QZNV94PHfDuJKhZxoqICMjA9axsYYy9EKG3RlsDUSJJWKT2h1+KoIvkOj4MJ1iUFS4avELhONwHVOJaYkgR2XbRyNsE2oMpxWFs5dJQiNDLgKcKRVgRWiC9pBFyzzJ7WrOLI3J7Rz8+vpRqvV/Oue7tevu7+fqq6Vdmeme17/e+/Xv/eeCAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAYMUQR9DTcXIrNxWku9eYy1lwmKRYAAAAAAADALgQ7Oys3l03SCnLOVm0uG4WgZ945HY6PuNQocgAAAAAAAAS1vbk0eiwHpJXxiXzRfV5pLhN9HCNxLHrcldgNAAAAAAAA6Ec/gU4CnvlU9PZ5w4KlzO4AAAAAAABAN0ECnQQ882eL2BHo9JcSuwQAAAAAAADthAl0EvDMj6LYFejUZSe7BQAAAAAAALMNEugk4JkPJbEv2DnBbgEAAAAAAMBMUQQ6/WW8uQxTpJlUEvuCnQ12CwAAAAAAAFRck81ott0IxZs5JSHYCQAAAAAAAAtp9qUGJeMMQlUo5kwpCcFOAAAAAAAAWKYi5gJRe4VxPLOiJAQ7AQAAAAAAYAlHWsHHJCaRoVt7+pWEYCcAAAAAAAAsUJH4u633k+XpsCtSqyQEOwEAAAAAAJAgzajUGdJtCk5VhK7taVQSgp0AAAAAAABIQEmi7rI+d27UXdsrQtAzbccUwU4AAAAAAAAYU5YYxuU85w/ubJxzxx2NoYUL4xjPsyIEPdOgJAQ7AQAAAAAArDaUge/gSCvIuVaiHhNz3jyZ87nPy9CVi6Xx8iGRqSk59d3vSuPIkTi+R7W57GguNQ5LK5WkFUjnHAYAAAAAALBUWgMljrTG49QA53AsBeMsknM00HnhAvf/brDTM/3DH8r0M8/E9d3qzWVUWoHPMQ5Ra5SEYCcAAAAAAIDV0hQoKTWX1d7P4ThXVFi+Us5Ze5ub2embGex0/79/v5x6/HE32zNGk9IKfO6TVsZnnUM20eOPYCcAAAAAAIDFggRKNMA4c2zJWkzbVPTWpcuSGf+O37x5bpBTg52zzQ52uiYmWt3ajx0ztb/q0sr2POj99P8fh9n7W9czmeNzpSQEOwEAAAAAAKzWT6Ck0lzWS+dJdGYGwfa1+Xttxr/9QOZMS7zft/ubuYK4crGc8wd3vt1tfba2wU7P9J497pKgupzO+py9DyblzIDocJt9uWTG70pd1lNtLpslnxmmWi4EOwEAAAAAACzWLVCiwS8N7gxnugQ0m/OTN0vhd1d1fVm3YKf792PH3LE8Y5q8yCYaPF0h+RtPtCQEOwEAAAAAAKzWKVCipblsyPKXbzc2Zye9gp1vv87MWJ5J04DnIslXt/aSEOwEAAAAAACwWqdAidNcxjP7pbXL+trbRGdc71e/wU7X1JQ7W7s7Y3t2g54bm8vWHJ0rJSHYCQAAAAAAAAtp9qUGJeMMQlUo5kwpCcFOAAAAAAAAWKYi5gJRe4VxPLOiJAQ7AQAAAAAAYAlHWsHHJCaRoVt7+pWEYCcAAAAAAAAsUJH4u633k+XpsCtSqyQEOwEAAAAAAJAgzajUGdJtCk5VhK7taVQSgp0AAAAAAABIQEmi7rI+d27UXdsrQtAzbccUwU4AAAAAAAAYU5YYxuU85w/ubJxzxx2NoYUL4xjPsyIEPdOgJAQ7AQAAAAAArDaUge/gSCvIuVaiHhNz3jyZ87nPy9CVi6Xx8iGRqSk59d3vSuPIkTi+R7W57GguNQ5LK5WkFUjnHAYAAAAAALBUWgMljrTG49QA53AsBeMsknM00HnhAvf/brDTM/3DH8r0M8/E9d3qzWVUWoHPMQ5Ra5SEYCcAAAAAAIDV0hQoKTWX1d7P4ThXVFi+Us5Ze5ub2embGex0/79/v5x6/HE32zNGk9IKfO6TVsZnnUM20eOPYCcAAAAAAIDFggRKNMA4c2zJWkzbVPTWpcuSGf+O37x5bpBTg52zzQ52uiYmWt3ajx0ztb/q0sr2POj99P8fh9n7W9czmeNzpSQEOwEAAAAAAKzWT6Ck0lzWS+dJdGYGwfa1+Xttxr/9QOZMS7zft/ubuYK4crGc8wd3vt1tfba2wU7P9J497pKgupzO+py9DyblzIDocJt9uWTG70pd1lNtLpslnxmmWi4EOwEAAAAAACzWLVCiwS8N7gxnugQ0m/OTN0vhd1d1fVm3YKf792PH3LE8Y5q8yCYaPF0h+RtPtCQEOwEAAAAAAKzWKVCipblsyPKXbzc2Zye9gp1vv87MWJ5J04DnIslXt/aSEOwEAAAAAACwWqdAidNcxjP7pbXL+trbRGdc71e/wU7X1JQ7W7s7Y3t2g54bm8vWHJ0rJSHYCQAAAAAAAAtp9qUGJeMMQlUo5kwpCcFOAAAAAAAAWKYi5gJRe4VxPLOiJAQ7AQAAAAAAYAlHWsHHJCaRoVt7+pWEYCcAAAAAAAAsUJH4u633k+XpsCtSqyQEOwEAAAAAAJAgzajUGdJtCk5VhK7taVQSgp0AAAAAAABIQEmi7rI+d27UXdsrQtAzbccUwU4AAAAAAAAYU5YYxuU85w/ubJxzxx2NoYUL4xjPsyIEPdOgJAQ7AQAAAAAArDaUge/gSCvIuVaiHhNz3jyZ87nPy9CVi6Xx8iGRqSk59d3vSuPIkTi+R7W57GguNQ5LK5WkFUjnHAYAAAAAALBUWgMljrTG49QA53AsBeMsknM00HnhAvf/brDTM/3DH8r0M8/E9d3qzWVUWoHPMQ5Ra5SEYCcAAAAAAIDV0hQoKTWX1d7P4ThXVFi+Us5Ze5ub2embGex0/79/v5x6/HE32zNGk9IKfO6TVsZnnUM20eOPYCcAAAAAAIDFggRKNMA4c2zJWkzbVPTWpcuSGf+O37x5bpBTg52zzQ52uiYmWt3ajx0ztb/q0sr2POj99P8fh9n7W9czmeNzpSQEOwEAAAAAAKzWT6Ck0lzWS+dJdGYGwfa1+Xttxr/9QOZMS7zft/ubuYK4crGc8wd3vt1tfba2wU7P9J497pKgupzO+py9DyblzIDocJt9uWTG70pd1lNtLpslnxmmWi4EOwEAAAAAACzWLVCiwS8N7gxnugQ0m/OTN0vhd1d1fVm3YKf792PH3LE8Y5q8yCYaPF0h+RtPtCQEOwEAAAAAAKzWKVCipblsyPKXbzc2Zye9gp1vv87MWJ5J04DnIslXt/aSEOwEAAAAAACwWqdAidNcxjP7pbXL+trbRGdc71e/wU7X1JQ7W7s7Y3t2g54bm8vWHJ0rJSHYCQAAAAAAALAIAAAAAAAAAIBMINgJAAAAAAAAIBMIdgIAAAAAAADIBIKdAAAAAAAAADKBYCcAAAAAAACATCDYCQAAAAAAACAT5qR544vnnisfX3CBLCnOl+ULfsP93ccvvOCM1/zojTfdn/te/2c5OHlcfvT6mzJ58qSxbVzyznfK8ksucX9eOn++OM1Ff/oOvvaaTP7bv7k/Xzl+XPYdPer+GwAAAAByxpm1qEtn/LuXfd7PyeYyNuMnACBHUhfs1MDijRcvlDsuf48b5OzFD37ODIL+4NVjzeXn7s84Ap8a3Lz1N39Tbnzf+6Q4d27X12oQ1H+PT4Oeuw4flvuff979N2C50gDvrXtLFivpg6qlYBthft8Vm8swRWz1NSbK88tvqMPsvcnkOY30XSs5HqI9T3U/LfF+Dsd47o9594OD3j6sZbxc03oP5f6czXp8Ftt81OctP35SE+y8dN55cvcH3+8GOjXgOQj9DF0mTy6WBw4faS4vRxL01IDl3R/72BnB31Ffp9up2d3LIuD89BsAwAAAAFIL+TrTXi5rlr5dK1F+2U3n+TktrpvcFAAAAfQZ7UjjTXi5rlr5dK1F+2U3n+TktrpvcFAAAAfQZ7UjjTXi5rlr5dK1F+2U3n+TktrpvcFAAAA'
    try { doc.addImage(logoBase64, 'PNG', 14, 10, 55, 11) } catch (_) { /* fallback if image fails */ }

    // -- Header line under logo --
    const headerY = 26
    doc.setDrawColor(45, 212, 191)
    doc.setLineWidth(0.8)
    doc.line(14, headerY, pageWidth - 14, headerY)

    // -- Title + metadata --
    doc.setTextColor(30, 30, 30)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('Call Report', 14, headerY + 10)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(`Date Range: ${start}  to  ${end}`, 14, headerY + 17)
    doc.text(`Total Calls: ${records.length}`, 14, headerY + 23)

    // -- Generated timestamp on the right --
    const now = new Date()
    const genStr = `Generated: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
    doc.setFontSize(8)
    doc.text(genStr, pageWidth - 14, headerY + 17, { align: 'right' })

    // -- Table --
    let pageNum = 0
    autoTable(doc, {
      startY: headerY + 30,
      head: [['#', 'Timestamp', 'Duration', 'Caller Number']],
      body: records.map((r, i) => [
        (i + 1).toString(),
        r.timestamp,
        r.duration || '',
        formatPhone(r.phone_number),
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.25,
      },
      headStyles: {
        fillColor: [30, 30, 30],
        textColor: [45, 212, 191],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30 },
        3: { cellWidth: 50 },
      },
      didDrawPage: () => {
        pageNum++
        const pageH = doc.internal.pageSize.getHeight()
        doc.setDrawColor(45, 212, 191)
        doc.setLineWidth(0.5)
        doc.line(14, pageH - 15, pageWidth - 14, pageH - 15)
        doc.setFontSize(7)
        doc.setTextColor(150, 150, 150)
        doc.text('Intellasure - Call Report', 14, pageH - 10)
        doc.text(
          `Page ${pageNum}`,
          pageWidth - 14,
          pageH - 10,
          { align: 'right' }
        )
      },
    })

    doc.save(`call_report_${start}_to_${end}.pdf`)
  }, [records, getDateRange])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-3xl max-h-[85vh] bg-dark-card border border-dark-border rounded-2xl shadow-2xl flex flex-col animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-accent-teal" />
            <h2 className="text-white font-bold text-lg">Call Report</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-dark-card-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-red-400" />
          </button>
        </div>

        {/* Date Range Selector */}
        <div className="px-6 py-4 border-b border-dark-border">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-300 font-medium">
              Date Range
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                { key: 'today', label: 'Today' },
                { key: '7d', label: 'Last 7 Days' },
                { key: '30d', label: 'Last 30 Days' },
                { key: 'custom', label: 'Custom' },
              ] as { key: RangePreset; label: string }[]
            ).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setRangePreset(opt.key)}
                className={`px-3 py-1.5 text-sm rounded-xl font-medium transition-colors ${
                  rangePreset === opt.key
                    ? 'bg-accent-teal text-black'
                    : 'bg-dark-input border border-dark-border text-gray-300 hover:border-gray-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {rangePreset === 'custom' && (
            <div className="flex items-center gap-3 mt-3">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-1.5 bg-dark-input border border-dark-border rounded-xl text-white text-sm focus:border-accent-teal outline-none"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 bg-dark-input border border-dark-border rounded-xl text-white text-sm focus:border-accent-teal outline-none"
              />
            </div>
          )}
          <button
            onClick={fetchRecords}
            disabled={loading}
            className="mt-3 px-4 py-2 bg-accent-teal text-black text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Get Call Report'}
          </button>
          {error && (
            <p className="mt-2 text-red-400 text-sm">{error}</p>
          )}
        </div>

        {/* Results */}
        {fetched && (
          <>
            <div className="flex items-center justify-between px-6 py-3 border-b border-dark-border">
              <span className="text-sm text-gray-400">
                {records.length} call{records.length !== 1 ? 's' : ''}
              </span>
              {records.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={downloadCSV}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-input border border-dark-border rounded-xl text-sm text-gray-300 hover:border-accent-teal hover:text-white transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    CSV
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-input border border-dark-border rounded-xl text-sm text-gray-300 hover:border-accent-teal hover:text-white transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    PDF
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto px-6 py-3">
              {records.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  No calls for the selected date range.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-dark-border">
                      <th className="pb-2 pr-4 font-medium">Timestamp</th>
                      <th className="pb-2 pr-4 font-medium">Duration</th>
                      <th className="pb-2 font-medium">Caller Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, i) => (
                      <tr
                        key={r.call_id + '-' + i}
                        className="border-b border-dark-border/50 hover:bg-dark-card-hover transition-colors"
                      >
                        <td className="py-2.5 pr-4 text-gray-300">
                          {r.timestamp}
                        </td>
                        <td className="py-2.5 pr-4 text-gray-300">
                          {r.duration || '-'}
                        </td>
                        <td className="py-2.5 text-white font-mono">
                          {r.phone_number ? formatPhone(r.phone_number) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CallSheetModal
